import React, { useState, useEffect } from 'react';
import './PMDateEditModal.css';

const PMDateEditModal = ({ 
  isOpen, 
  onClose, 
  pmData = null, 
  onSave 
}) => {
  const [formData, setFormData] = useState({
    current_date: '',
    new_date: '',
    calculated_date: '',
    use_calculated: true,
    notes: ''
  });
  
  const [lastServiceDate, setLastServiceDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // ฟังก์ชันคำนวณวันที่ใหม่ (วันที่ล่าสุด + 90 วัน)
  const calculateNextPMDate = (lastDate) => {
    if (!lastDate) return '';
    
    const date = new Date(lastDate);
    if (isNaN(date.getTime())) return '';
    
    // เพิ่ม 90 วัน
    date.setDate(date.getDate() + 90);
    return date.toISOString().split('T')[0];
  };

  // ฟังก์ชันแปลงวันที่เป็นรูปแบบไทย
  const formatThaiDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  // ฟังก์ชันดึงข้อมูลวันที่ดำเนินการล่าสุด
  useEffect(() => {
    if (!isOpen || !pmData) return;
    (async () => {
      try {
        // 1) Resolve the accurate current due date from server by schedule id when possible
        let currentDueDate = pmData?.due_date?.split('T')[0] || '';
        if (pmData.pm_schedule_id && pmData.serial_number) {
          try {
            const listResp = await fetch(`/api/pm-service/schedules?status=all&serial=${encodeURIComponent(pmData.serial_number)}`);
            if (listResp.ok) {
              const listData = await listResp.json();
              const found = Array.isArray(listData) ? listData.find(s => String(s.id) === String(pmData.pm_schedule_id)) : null;
              if (found?.next_inspection_date) {
                currentDueDate = String(found.next_inspection_date).split('T')[0];
              }
            }
          } catch (e) {
            console.warn('Could not resolve schedule by id, using provided due_date');
          }
        }

        // 2) Fetch last service date for the serial
        let lastDate = currentDueDate;
        const resp = await fetch(`/api/pm-service/last-service-date/${pmData.serial_number}`);
        if (resp.ok) {
          const data = await resp.json();
          lastDate = data.last_service_date || lastDate;
        }
        setLastServiceDate(lastDate);

        // 3) Compute recommended next date (+90 days from last service)
        const calculatedDate = calculateNextPMDate(lastDate);
        setFormData({
          current_date: currentDueDate,
          new_date: calculatedDate,
          calculated_date: calculatedDate,
          use_calculated: true,
          notes: `อัปเดตอัตโนมัติจากวันที่ดำเนินการล่าสุด: ${formatThaiDate(lastDate)}`
        });
      } catch (e) {
        console.error('Error initializing PMDateEditModal:', e);
      }
    })();
  }, [isOpen, pmData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // ถ้าเปลี่ยนจาก calculated date ให้อัปเดต new_date
    if (name === 'use_calculated') {
      setFormData(prev => ({
        ...prev,
        new_date: checked ? prev.calculated_date : prev.current_date
      }));
    }

    // Clear errors
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.new_date) {
      newErrors.new_date = 'กรุณาเลือกวันที่ใหม่';
    }
    
    // ตรวจสอบว่าวันที่ใหม่ไม่ใช่อดีต
    const newDate = new Date(formData.new_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (newDate < today) {
      newErrors.new_date = 'วันที่ใหม่ไม่ควรเป็นวันที่ในอดีต';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    if (!pmData?.pm_schedule_id) {
      setErrors({ submit: 'ไม่พบ PM Schedule สำหรับอัปเดต กรุณาตรวจสอบจำนวนครั้ง PM หรือสร้างตาราง PM รอบถัดไป' });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const updateData = {
        pm_schedule_id: pmData.pm_schedule_id,
        serial_number: pmData.serial_number,
        old_date: formData.current_date,
        new_date: formData.new_date,
        last_service_date: lastServiceDate,
        calculated_date: formData.calculated_date,
        use_calculated: formData.use_calculated,
        notes: formData.notes,
        updated_by: 1 // TODO: Get current user ID
      };
      
      await onSave(updateData);
      // onClose() will be called from handleSavePMDate if successful
      
    } catch (error) {
      console.error('Error updating PM date:', error);
      setErrors({ submit: 'เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pm-date-edit-modal-overlay" onClick={handleClose}>
      <div className="pm-date-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pm-date-edit-modal-header">
          <h2>📅 แก้ไขวันที่กำหนด PM</h2>
          <button 
            className="pm-date-edit-modal-close" 
            onClick={handleClose}
            disabled={isLoading}
          >
            ×
          </button>
        </div>
        
        <div className="pm-date-edit-modal-content">
          {/* ข้อมูลอุปกรณ์ */}
          <div className="device-info">
            <h3>ข้อมูลอุปกรณ์</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>หมายเลขซีเรียล:</label>
                <span>{pmData?.serial_number}</span>
              </div>
              <div className="info-item">
                <label>ลูกค้า:</label>
                <span>{pmData?.customer_name}</span>
              </div>
              <div className="info-item">
                <label>ประเภทสินค้า:</label>
                <span>{pmData?.product_type}</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="pm-date-edit-form">
            {/* วันที่ปัจจุบัน */}
            <div className="form-section">
              <h3>วันที่ปัจจุบัน</h3>
              <div className="current-date-display">
                <div className="date-info">
                  <span className="date-label">วันที่กำหนดปัจจุบัน:</span>
                  <span className="date-value">{formatThaiDate(formData.current_date)}</span>
                </div>
                <div className="date-info">
                  <span className="date-label">วันที่ดำเนินการล่าสุด:</span>
                  <span className="date-value">{formatThaiDate(lastServiceDate)}</span>
                </div>
              </div>
            </div>

            {/* วันที่ใหม่ที่คำนวณได้ */}
            <div className="form-section">
              <h3>วันที่ใหม่ที่แนะนำ</h3>
              <div className="calculated-date-section">
                <div className="calculated-date-display">
                  <div className="calculation-info">
                    <span className="calculation-label">วันที่คำนวณอัตโนมัติ:</span>
                    <span className="calculation-formula">
                      {formatThaiDate(lastServiceDate)} + 90 วัน
                    </span>
                  </div>
                  <div className="calculated-result">
                    <span className="result-date">{formatThaiDate(formData.calculated_date)}</span>
                  </div>
                </div>
                
                <div className="use-calculated-option">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="use_calculated"
                      checked={formData.use_calculated}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    ใช้วันที่ที่คำนวณอัตโนมัติ
                  </label>
                </div>
              </div>
            </div>

            {/* เลือกวันที่ใหม่ */}
            <div className="form-section">
              <h3>วันที่ใหม่</h3>
              <div className="form-group">
                <label htmlFor="new_date">วันที่กำหนดใหม่ *</label>
                <input
                  type="date"
                  id="new_date"
                  name="new_date"
                  value={formData.new_date}
                  onChange={handleInputChange}
                  className={errors.new_date ? 'error' : ''}
                  disabled={isLoading}
                  min={new Date().toISOString().split('T')[0]}
                />
                {errors.new_date && (
                  <span className="error-message">{errors.new_date}</span>
                )}
                {formData.new_date && (
                  <div className="selected-date-preview">
                    <span className="preview-label">วันที่ที่เลือก:</span>
                    <span className="preview-date">{formatThaiDate(formData.new_date)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* หมายเหตุ */}
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="notes">หมายเหตุ</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="เพิ่มหมายเหตุเกี่ยวกับการเปลี่ยนแปลงวันที่..."
                  rows="3"
                  disabled={isLoading}
                />
              </div>
            </div>

            {errors.submit && (
              <div className="error-message submit-error">{errors.submit}</div>
            )}

            <div className="pm-date-edit-modal-actions">
              <button 
                type="button" 
                className="btn-cancel" 
                onClick={handleClose}
                disabled={isLoading}
              >
                ยกเลิก
              </button>
              <button 
                type="submit" 
                className="btn-save"
                disabled={isLoading || !pmData?.pm_schedule_id}
              >
                {isLoading ? 'กำลังบันทึก...' : 'บันทึกการแก้ไข'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PMDateEditModal;